import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Spin, Table, Typography, App } from "antd";
import { CloudDownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { API_URL } from "../../shared/utils/Util";
import {
  getFetchT,
  shortDisplayTime,
  centsToDollar,
  downloadAsCsv,
} from "../../shared/utils/TypedUtil";
import { GetAbbreviatedOrdersResponse, AbbreviatedOrder } from "../../types/api";

const { Title } = Typography;

export type OrderTableProps = {
  restaurantId: string;
  start_time: Dayjs;
  end_time: Dayjs;
};

export function OrderTable({ restaurantId, start_time, end_time }: OrderTableProps) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [orders, setOrders] = React.useState<ReadonlyArray<AbbreviatedOrder> | null>(null);

  React.useEffect(() => {
    // Go into loading state first.
    setOrders(null);

    getFetchT<GetAbbreviatedOrdersResponse>(
      `${API_URL}/v1/r/${restaurantId}/stats/${start_time.format()}/${end_time.format()}/orders`,
      (resp: GetAbbreviatedOrdersResponse) => setOrders(resp.abbreviated_orders),
      (err: any) => {
        console.log("failed to get abbrebiated orders for stats", err);
        notification.error({
          message: t("error_occurred"),
          description: t("please_try_again_later"),
        });
      }
    );
  }, [restaurantId, start_time, end_time, t]);

  const columns = [
    {
      title: "Payment Processor",
      dataIndex: "payment_processor",
      key: "payment_processor",
    },
    {
      title: "Download",
      key: "download",
      render: (_: unknown, record: { payment_processor: string }) => {
        const pp = record.payment_processor;

        const rowsToExport = [...(orders ?? [])]
          .sort((o1, o2) => (o1.create_time > o2.create_time ? 1 : -1))
          .filter((o) => o.payment_processor === pp)
          .map((o) => ({
            "Order Type": o.pos_order_type,
            "Order Name": o.pos_order_name,
            "Create Time": dayjs(o.create_time).format("YYYY/MM/DD HH:mm"),
            "Pretax Price": o.pretax_price == null ? null : centsToDollar(o.pretax_price),
            Tip: o.tip == null ? null : centsToDollar(o.tip),
            "Handling Fee": o.handling_fee == null ? null : centsToDollar(o.handling_fee),
            "Payment Processor": o.payment_processor,
          }));

        const makeCsv = () => {
          downloadAsCsv(
            `${pp}_${start_time.format("YYYYMMDD")}_${end_time.format("YYYYMMDD")}.csv`,
            rowsToExport
          );
        };

        return (
          <Button
            icon={<CloudDownloadOutlined />}
            size="large"
            type="primary"
            shape="round"
            onClick={makeCsv}
          />
        );
      },
    },
  ];

  return (
    <>
      <Title level={4}>Payments</Title>
      {orders == null ? (
        <Spin size="large" />
      ) : (
        <Table columns={columns} dataSource={[{ payment_processor: "STRIPE" }]} />
      )}
    </>
  );
}
